import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { MapPin, Home as HomeIconLucide, ArrowRight, Users, Award, MessageCircle, Star, CheckCircle, Clock, Phone, Mail, Search, Filter, BedDouble, Bath, CarFront, Maximize } from 'lucide-react';
import NewsletterSignup from '@/components/NewsletterSignup';
import OptimizedImage from '@/components/OptimizedImage';
import SEO from '@/components/SEO';
import { getProperties } from '@/lib/supabaseUtils';
import { formatPropertyPrice } from '@/lib/propertyUtils';
import { useContent } from '@/lib/useContent';
import { useLocalizedProperty } from '@/lib/useLocalizedProperty';

const Home = () => {
  const { getContent } = useContent();
  const { t, i18n } = useTranslation();
  const { getTitle, getLocation, getDescription } = useLocalizedProperty();
  const [featuredProperties, setFeaturedProperties] = useState([]);

  useEffect(() => {
    const loadFeaturedProperties = async () => {
      try {
        const allProperties = await getProperties();
        const featured = allProperties.slice(0, 3);
        setFeaturedProperties(featured);
      } catch (error) {
        console.error('Error loading featured properties:', error);
        setFeaturedProperties([]);
      }
    };
    loadFeaturedProperties();
  }, []);



  return (
    <div className="space-y-2 md:space-y-4">
      <SEO
        title="Buy or Rent a House in Roatán, Honduras"
        description="Buy or rent a house in Roatán, the Bay Islands & Honduras. Roatán Luxury Homes – Caribbean real estate: luxury homes, villas, beachfront in Roatan."
        canonical="/"
      />
      {/* Enhanced Hero Section - Compact & Full Width */}
      <section className="hero-full-bleed relative -mt-6 py-16 md:py-20 lg:py-24 flex items-center justify-center text-center overflow-hidden w-full lg:w-screen lg:left-1/2 lg:right-1/2 lg:-translate-x-1/2 lg:mx-0">
        {/* Desktop Background */}
        <div className="hidden lg:block absolute inset-0 w-full">
          <OptimizedImage 
            alt="Tropical Caribbean beach background" 
            className="w-full h-full object-cover" 
            src="/Photos/hero-banner-optimized.jpg" 
            webpSrc="/Photos/hero-banner-optimized.webp"
            loading="eager" 
            fetchpriority="high"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-turquoise-dark/80 to-primary/70"></div>
        </div>
        
        {/* Mobile Background */}
        <div className="lg:hidden absolute inset-0 w-full bg-gradient-to-br from-turquoise-dark via-primary to-turquoise-light"></div>
        
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-0 mx-auto max-w-7xl">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 text-white text-sm font-medium mb-4">
              <Star className="w-4 h-4" />
              <span>{t('common.trustedBy')}</span>
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight">
            {getContent('home', 'hero', 'title')}
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl text-white/95 mb-10 max-w-4xl mx-auto drop-shadow-md">
            {getContent('home', 'hero', 'subtitle')}
          </p>
          
          {/* Desktop Hero Actions */}
          <div className="hidden lg:flex items-center justify-center space-x-6 mb-12">
            <Button size="lg" asChild variant="outline" className="border-white text-white hover:bg-white hover:text-primary shadow-lg text-lg px-8 py-4 bg-white/20 backdrop-blur-sm font-semibold">
              <Link to="/properties">
                <Search className="mr-2 h-5 w-5" />
                {t('common.browseProperties')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" asChild variant="outline" className="border-white text-white hover:bg-white hover:text-primary shadow-lg text-lg px-8 py-4 bg-white/20 backdrop-blur-sm font-semibold">
              <Link to="/contact">
                <Phone className="mr-2 h-5 w-5" />
                {t('common.contactUs')}
              </Link>
            </Button>
          </div>
          
          {/* Mobile Hero Actions */}
          <div className="lg:hidden space-y-4 mb-8">
            <Button size="lg" asChild variant="outline" className="border-white text-white hover:bg-white hover:text-primary shadow-md w-full bg-white/20 backdrop-blur-sm font-semibold">
              <Link to="/properties">{t('common.viewProperties')} <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
            <Button size="lg" asChild variant="outline" className="border-white text-white hover:bg-white hover:text-primary shadow-md w-full bg-white/20 backdrop-blur-sm font-semibold">
              <Link to="/contact">{t('common.contactUs')}</Link>
            </Button>
          </div>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-6 text-white/90 text-sm lg:text-base">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>{getContent('home', 'hero', 'trustIndicator1')}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>{getContent('home', 'hero', 'trustIndicator2')}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>{getContent('home', 'hero', 'trustIndicator3')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Text Section */}
      <section className="container mx-auto px-4 py-4 md:py-6 lg:py-8">
        <div className="text-center max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-primary">{getContent('home', 'welcome', 'title')}</h2>
          <p className="text-lg lg:text-xl text-muted-foreground mb-0">
            {getContent('home', 'welcome', 'description')}
          </p>
        </div>
      </section>

      {/* Features & Image Section */}
      <section className="container mx-auto px-4 py-4 md:py-6 lg:py-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
          {/* Desktop: Left - Feature Cards */}
          <div className="hidden lg:grid lg:grid-cols-1 gap-6">
            {[
              { icon: <HomeIconLucide className="w-8 h-8 text-primary" />, title: 'Expert Guidance', description: 'Navigating the Caribbean market with local expertise and personalized service.' },
              { icon: <Users className="w-8 h-8 text-primary" />, title: 'Client Focused', description: 'Your dreams are our priority. We listen, advise, and deliver exceptional results.' },
              { icon: <Award className="w-8 h-8 text-primary" />, title: 'Prime Locations', description: 'Access to exclusive listings in the Caribbean\'s most sought-after destinations.' },
            ].map((item, index) => {
              // Get dynamic content for this feature
              const dynamicTitle = getContent('home', 'welcome', `feature${index + 1}Title`) || item.title;
              const dynamicDescription = getContent('home', 'welcome', `feature${index + 1}Desc`) || item.description;
              
              return (
                              <div key={item.title} className="flex items-start gap-4 p-4 bg-card rounded-xl shadow-md hover:shadow-lg border border-border/50">
                  <div className="flex-shrink-0">{item.icon}</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-foreground">{dynamicTitle}</h3>
                    <p className="text-muted-foreground">{dynamicDescription}</p>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Desktop: Right Image */}
          <div className="hidden lg:block">
            <div className="relative">
              <OptimizedImage
                src="/Photos/roatan-island-optimized.jpg"
                webpSrc="/Photos/roatan-island-optimized.webp"
                alt="Roatán Island"
                className="rounded-2xl shadow-2xl w-full h-96 object-cover"
              />
              <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg">
                <div className="text-sm font-semibold text-primary">{getContent('home', 'featuredLocation', 'startingPrice')}</div>
              </div>
            </div>
          </div>
          
          {/* Mobile: Centered Layout */}
          <div className="lg:hidden">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              {[
                { icon: <HomeIconLucide className="w-12 h-12 text-primary mx-auto mb-4" />, title: 'Expert Guidance', description: 'Navigating the Caribbean market with local expertise and personalized service.' },
                { icon: <Users className="w-12 h-12 text-primary mx-auto mb-4" />, title: 'Client Focused', description: 'Your dreams are our priority. We listen, advise, and deliver exceptional results.' },
                { icon: <Award className="w-12 h-12 text-primary mx-auto mb-4" />, title: 'Prime Locations', description: 'Access to exclusive listings in the Caribbean\'s most sought-after destinations.' },
              ].map((item, index) => {
                // Get dynamic content for this feature
                const dynamicTitle = getContent('home', 'welcome', `feature${index + 1}Title`) || item.title;
                const dynamicDescription = getContent('home', 'welcome', `feature${index + 1}Desc`) || item.description;
                
                return (
                <div 
                  key={item.title} 
                  className="p-6 bg-card rounded-xl shadow-md hover:shadow-lg border border-border/50"
                >
                  {item.icon}
                  <h3 className="text-xl font-semibold mb-2 text-foreground">{dynamicTitle}</h3>
                  <p className="text-muted-foreground">{dynamicDescription}</p>
                </div>
              );
            })}
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Featured Properties Section - Desktop Larger Cards */}
      <section className="container mx-auto px-4 py-8 md:py-12 lg:py-16">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-primary">
          {featuredProperties.length > 0 ? t('home.featuredProperties') : t('nav.properties')}
        </h2>
        <p className="text-lg lg:text-xl text-center text-muted-foreground mb-12 max-w-4xl mx-auto">
          {featuredProperties.length > 0 
            ? t('home.featuredPropertiesDesc')
            : t('home.noPropertiesDesc')
          }
        </p>
        
        {featuredProperties.length > 0 ? (
          <>
            {/* Desktop: Larger Grid */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-8">
              {featuredProperties.map((property, index) => (
                <Link key={property.id} to={`/properties/${property.id}`} className="group block h-full">
                  <Card className="overflow-hidden shadow-lg hover:shadow-xl border border-border/50 h-full cursor-pointer">
                    <div className="relative h-64 overflow-hidden">
                      <img 
                        alt={getTitle(property)} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        src={property.image || "https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8cmVhbCUyMGVzdGF0ZXxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60"} 
                        loading="lazy" 
                      />
                      {property.type && (
                        <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                          {property.type}
                        </div>
                      )}
                      {index === 0 && (
                        <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                          {t('home.featured')}
                        </div>
                      )}
                    </div>
                    <CardContent className="p-8">
                      <CardTitle className="text-2xl mb-3">{getTitle(property)}</CardTitle>
                      <div className="flex items-center text-muted-foreground mb-4">
                        <MapPin className="w-5 h-5 mr-2" />
                        <span className="text-lg">{getLocation(property)}</span>
                      </div>
                      <p className="text-muted-foreground mb-6 text-lg line-clamp-3">{getDescription(property)}</p>
                      
                      {/* Property Details */}
                      <div className="flex items-center gap-6 mb-6 text-sm text-muted-foreground">
                        {property.beds && (
                          <div className="flex items-center gap-2">
                            <BedDouble className="w-5 h-5 text-primary" />
                            <span>{property.beds} {t('home.beds')}</span>
                          </div>
                        )}
                        {property.baths && (
                          <div className="flex items-center gap-2">
                            <Bath className="w-5 h-5 text-primary" />
                            <span>{property.baths} {t('home.baths')}</span>
                          </div>
                        )}
                        {property.area && (
                          <div className="flex items-center gap-2">
                            <Maximize className="w-5 h-5 text-primary" />
                            <span>{property.area} {t('common.sqFt')}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-primary font-bold text-xl">
                          {property.price ? formatPropertyPrice(property, i18n.language) : t('home.contactForPrice')}
                        </div>
                        <span className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-base font-medium text-primary-foreground hover:bg-primary/90">
                          {t('common.viewDetails')} 
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            
            {/* Mobile: Compact Grid */}
            <div className="lg:hidden grid md:grid-cols-3 gap-8">
              {featuredProperties.map((property, index) => (
                <Link key={property.id} to={`/properties/${property.id}`} className="group block">
                  <Card className="overflow-hidden shadow-md hover:shadow-lg border border-border/50 cursor-pointer">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        alt={getTitle(property)} 
                        className="w-full h-full object-cover" 
                        src={property.image || "https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8cmVhbCUyMGVzdGF0ZXxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60"} 
                        loading="lazy" 
                      />
                      {property.type && (
                        <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                          {property.type}
                        </div>
                      )}
                      {index === 0 && (
                        <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                          {t('home.featured')}
                        </div>
                      )}
                    </div>
                    <CardContent className="p-6">
                      <CardTitle className="text-xl mb-2">{getTitle(property)}</CardTitle>
                      <div className="flex items-center text-muted-foreground mb-3">
                        <MapPin className="w-4 h-4 mr-1" />
                        {getLocation(property)}
                      </div>
                      <p className="text-muted-foreground mb-4 line-clamp-2">{getDescription(property)}</p>
                      
                      {/* Property Details */}
                      <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
                        {property.beds && (
                          <div className="flex items-center gap-1">
                            <span>🛏️</span>
                            <span>{property.beds} {t('home.beds')}</span>
                          </div>
                        )}
                        {property.baths && (
                          <div className="flex items-center gap-1">
                            <span>🚿</span>
                            <span>{property.baths} {t('home.baths')}</span>
                          </div>
                        )}
                        {property.area && (
                          <div className="flex items-center gap-1">
                            <Maximize className="w-4 h-4 text-primary" />
                            <span>{property.area} {t('common.sqFt')}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-primary font-bold">
                          {property.price ? formatPropertyPrice(property, i18n.language) : t('home.contactForPrice')}
                        </div>
                        <span className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
                          {t('common.viewDetails')} <ArrowRight className="ml-1 h-4 w-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <Button asChild size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground text-lg px-8 py-4 bg-white/80 backdrop-blur-sm font-semibold shadow-lg">
                <Link to="/properties">
                  <Filter className="mr-2 h-5 w-5" />
                  {t('common.viewAllProperties')} 
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="text-6xl mb-4">🏠</div>
              <h3 className="text-xl font-semibold mb-4">{t('home.noPropertiesYet')}</h3>
              <p className="text-muted-foreground mb-6">
                {t('home.noPropertiesDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild variant="outline">
                  <Link to="/contact">{t('common.contactUs')}</Link>
                </Button>
                <Button asChild className="bg-primary-dark hover:bg-primary-dark/90 text-primary-foreground">
                  <Link to="/submit-property">{t('home.listYourProperty')}</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Testimonials Section */}
      <section className="container mx-auto px-4" id="testimonials">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-primary">
          {getContent('home', 'testimonials', 'title')}{' '}
          <Link to="/contact" className="inline-flex items-center gap-1 text-lg md:text-xl font-semibold text-primary hover:underline">
            {t('home.testimonialsMessage')}
          </Link>
        </h2>
        <p className="text-lg lg:text-xl text-center text-muted-foreground mb-12 max-w-4xl mx-auto">
          {getContent('home', 'testimonials', 'subtitle')}
        </p>
        
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              name: 'Sarah & Mike',
              location: 'Roatán Beach Villa',
              text: 'Roatán Luxury Homes made our dream of owning a beachfront property a reality. Their attention to detail was exceptional.',
              rating: 5
            },
            {
              name: 'David Rodriguez',
              location: 'West Bay Condo',
              text: 'Professional, responsive, and truly understands the local market. I couldn\'t be happier with my investment property.',
              rating: 5
            },
            {
              name: 'Emma Thompson',
              location: 'Sandy Bay Home',
              text: 'From the first consultation to closing, everything was seamless. They really care about their clients\' success.',
              rating: 5
            }
          ].map((testimonial, index) => {
            // Get dynamic content for this testimonial
            const dynamicName = getContent('home', 'testimonials', `testimonial${index + 1}Name`) || testimonial.name;
            const dynamicLocation = getContent('home', 'testimonials', `testimonial${index + 1}Location`) || testimonial.location;
            const dynamicText = getContent('home', 'testimonials', `testimonial${index + 1}Text`) || testimonial.text;
            
            return (
            <div 
              key={index}
              className="bg-card p-6 lg:p-8 rounded-xl shadow-md border border-border/50"
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-muted-foreground mb-4 italic text-lg">{dynamicText}</p>
              <div>
                <div className="font-semibold text-foreground text-lg">{dynamicName}</div>
                <div className="text-sm text-muted-foreground">{dynamicLocation}</div>
              </div>
            </div>
          );
        })}
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="container mx-auto px-4">
        <div className="relative rounded-xl p-8 md:p-12 lg:p-16 text-center text-white overflow-hidden">
          {/* Background Image - WebP + sizes to reduce payload (PageSpeed: was 335 KiB, ~247 KiB savings with modern format + responsive). */}
          <div className="absolute inset-0">
            <OptimizedImage 
              src="/Photos/boat-ocean-optimized.jpg" 
              webpSrc="/Photos/boat-ocean-optimized.webp"
              alt="Boat on Caribbean ocean" 
              className="w-full h-full object-cover"
              loading="lazy"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 904px"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/60 via-turquoise-dark/60 to-primary/40"></div>
          </div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white drop-shadow-lg">{getContent('home', 'cta', 'title')}</h2>
            <p className="text-lg lg:text-xl mb-8 text-white/95 max-w-3xl mx-auto drop-shadow-md">{getContent('home', 'cta', 'subtitle')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild variant="outline" className="border-white text-white hover:bg-white hover:text-primary shadow-md text-lg px-8 py-4 bg-white/20 backdrop-blur-sm font-semibold">
                <Link to="/properties">
                  <HomeIconLucide className="mr-2 h-5 w-5" />
                  {t('common.browseProperties')}
                </Link>
              </Button>
              <Button size="lg" asChild variant="outline" className="border-white text-white hover:bg-white hover:text-primary shadow-md text-lg px-8 py-4 bg-white/20 backdrop-blur-sm font-semibold">
                <Link to="/contact">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  {t('common.getInTouch')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4">
        <NewsletterSignup />
      </section>
    </div>
  );
};

export default Home;